import { useEffect } from 'react'

import { useEventTracking, TrackingEvent } from '@/telemetry'

/**
 * @description
 * When a feature flag is disabled, PostHog automatically removes the variant from the user.
 * When the feature flag is re-enabled, the user will be randomly assigned to a variant again, which may be different from their original variant.
 * Also, when a feature flags rollout conditions change - from 50/50 split to 33/33/34 split, for example - PostHog will also re-assign users.
 *
 * Sticky feature flags ensure users will have their original variant restored,
 * even after the rollout conditions changed and they get moved into a different variant.

 * Stickiness is necessary for certain experiments where you need the user to have a consistent experience.
 * If the experiment contains only very minor design or copy changes, then a sticky feature flag is not needed.
 * 
 * @link https://posthog.com/docs/user-guides/feature-flags/sticky-feature-flag
 *
 * @example
 * type ExampleExperimentVariant = 'control' | 'test'
 *
 * const exampleExperimentName = 'exp-sticky-example'
 * const exampleExperimentIdentifier = 'exp_sticky_example_variant'
 *
 * const useStickyExampleExperiment = () => {
 *    const variant = useExperimentFeatureFlagVariant<ExampleExperimentVariant>(exampleExperimentName)
 *
 *    // Make the feature flag sticky by saving the variant's value to the user's properties
 *    useMakeFeatureFlagSticky(exampleExperimentIdentifier, variant, exampleExperimentName)
 *
 *    return {
 *        variant,
 *    }
 * }
 *
 * @param stickyExperimentIdentifier Name of "Person Property" used to store the variant in user properties. Should be similar to experiment name but with underscores.
 * @param stickyVariantValue The variant value. Most commonly, 'test' or 'control'.
 * @param stickyExperimentName The name of the experiment/feature flag in PostHog. (e.g. 'exp-sticky-example')
 */
export const useMakeFeatureFlagSticky = <Variant>(
    stickyExperimentIdentifier: string,
    stickyVariantValue: Variant,
    stickyExperimentName: string
) => {
    const { capture } = useEventTracking()

    useEffect(() => {
        if (stickyVariantValue === undefined) return // Feature flag has been disabled. Don't save it to user properties.

        capture(TrackingEvent.StickyFeatureFlagSave, {
            feature_flag: stickyExperimentName,

            $set_once: {
                [stickyExperimentIdentifier]: stickyVariantValue,
            },
        })
    }, [capture, stickyExperimentIdentifier, stickyVariantValue, stickyExperimentName])
}
